import { useRef, useEffect, useState } from "react";
import Icon from '../Icon';
import IconChevronNext from '../../assets/icons/chevron-next.svg';
import IconChevronPrev from '../../assets/icons/chevron-prev.svg';
import {
  LeftButton,
  RightButton,
  HorizontalScrollWrapper,
  HorizontalScroll,
  DotWrapper,
  Dot
} from "./styles";
import { CSSProperties } from "styled-components";
import { useWindowSize } from "../../hooks/useWindowSize";

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => JSX.Element;
  slidesToShow?: number;
  scrollAmount?: number;
  animationSpeed?: number;
  showArrows?: boolean;
  cardWidth?: string;
  style?: CSSProperties;
  showBullets?: boolean
}

const Carousel = <T,>({
  items,
  renderItem,
  slidesToShow = 3,
  scrollAmount = 200,
  animationSpeed = 300,
  showArrows = true,
  cardWidth = undefined,
  style,
  showBullets = false
}: CarouselProps<T>) => {
  const { isMobile } = useWindowSize()
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const smoothScroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;

    const start = scrollRef.current.scrollLeft;
    const end = direction === "left" ? start - scrollAmount : start + scrollAmount;
    const startTime = performance.now();

    const animateScroll = (currentTime: number) => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / animationSpeed, 1);
      const easeInOutQuad = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      if (scrollRef.current) {
        scrollRef.current.scrollLeft = start + (end - start) * easeInOutQuad;
      }

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  };



  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (!el) return;

      let itemWidth: number;

      if (cardWidth?.endsWith("px")) {
        itemWidth = parseFloat(cardWidth);
      } else {
        // fallback aproximado si no se provee un cardWidth concreto
        itemWidth = el.scrollWidth / items.length;
      }

      const scrollLeft = el.scrollLeft;
      const index = Math.round(scrollLeft / itemWidth);
      setCurrentIndex(index);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [cardWidth, items?.length]);


  const totalItems = items?.length; //Math.ceil(items?.length / slidesToShow);

  return (
    <>
      {showBullets && (
        <DotWrapper style={{ ...style, paddingBottom: 0 }}>
          {Array.from({ length: totalItems }).map((_, i) => (
            <Dot key={i} active={i === currentIndex} />
          ))}
        </DotWrapper>
      )}


      <div>
        <HorizontalScrollWrapper>
          {showArrows && (
            <LeftButton onClick={() => smoothScroll("left")}>
              <Icon size="16px" icon={IconChevronPrev} />
            </LeftButton>
          )}

          <HorizontalScroll ref={scrollRef} cardWidth={cardWidth} style={style}>
            {items && items.map((item, index) => renderItem(item, index))}
          </HorizontalScroll>

          {showArrows && (
            <RightButton onClick={() => smoothScroll("right")}>
              <Icon size="16px" icon={IconChevronNext} />
            </RightButton>
          )}
        </HorizontalScrollWrapper>
      </div>
    </>
  );
};

export default Carousel;
